import React from 'react';
import { useCallback, useState } from 'react';

export type FieldError = string | React.ReactNode | boolean | null | undefined;

export type FieldValidation<T, TError extends FieldError = FieldError> = (value: T) => TError;

interface FieldState<T, TError extends FieldError = FieldError> {
  value: T;
  touched: boolean;
  error: TError;
}

export interface Field<T, TError extends FieldError = FieldError> extends FieldState<T, TError> {
  name: string;
}

export function useField<T, TError extends FieldError = FieldError>(
  name: string,
  value: T,
  validation?: FieldValidation<T, TError>,
): [Field<T, TError>, (value: T) => void, (error: TError) => void] {
  const [state, setState] = useState<FieldState<T, TError>>({
    value,
    touched: false,
    error: validation?.(value),
  });

  const handleChange = (value: T) => {
    const error = validation?.(value);
    setState({ value, error, touched: true });
  };

  const setError = (error: TError) => {
    setState(prev => ({ ...prev, error }));
  };

  return [{ ...state, name }, handleChange, setError];
}

export function useValidationSchema<T>(yupSchema: UnsafeAny): FieldValidation<T, string> {
  return (value: T) => {
    try {
      yupSchema.validateSync(value);
      return null;
    } catch (error) {
      return error.message;
    }
  };
}

export function useValidatedField<T>(
  name: string,
  value: T,
  yupSchema: UnsafeAny,
): [Field<T>, (value: T) => void, (error: string) => void] {
  const validation = useValidationSchema<T>(yupSchema);
  return useField<T>(name, value, validation);
}

type FormValues<Values extends Record<string, unknown>> = {
  [K in keyof Values]: Field<Values[K]> & {
    setValue(value: Values[K]): void;
    setError(error: FieldError): void;
  };
} & {
  setValue: <K extends keyof Values>(key: K, value: Values[K]) => void;
  setError: <K extends keyof Values>(key: K, error: FieldError) => void;
};

type FormValuesState<Values extends Record<string, unknown>> = {
  [K in keyof Values]: FieldState<Values[K]>;
};

export function useFormValues<Values extends Record<string, unknown>>(
  initialValues: Values,
  validation: (values: Values) => Record<keyof Values, FieldError>,
): FormValues<Values> {
  const [state, setState] = useState<FormValuesState<Values>>(() => {
    const result: UnsafeAny = {};
    const validationErrors = validation(initialValues);
    for (const [key, value] of Object.entries(initialValues)) {
      result[key] = {
        value,
        touched: false,
        error: validationErrors[key],
      };
    }
    return result as FormValuesState<Values>;
  });

  const handleChange = useCallback(
    <K extends keyof Values>(key: K, value: Values[K]) => {
      const values: UnsafeAny = {};
      for (const [k, v] of Object.entries(state)) {
        values[k] = k === key ? value : v.value;
      }
      const errors = validation(values as Values);
      setState(prev => {
        const result: UnsafeAny = {};
        for (const [k, v] of Object.entries(prev)) {
          result[k] = { ...v, error: errors[k] };
          if (k === key) {
            result[k].value = value;
            result[k].touched = true;
          }
        }
        return result;
      });
    },
    [state],
  );

  const setError = <K extends keyof Values>(key: K, error: FieldError) => {
    setState(prev => ({
      ...prev,
      [key]: { ...prev[key], error },
    }));
  };

  const result: UnsafeAny = {
    setValue: handleChange,
    setError,
  };
  for (const [key, value] of Object.entries(state)) {
    result[key] = {
      ...value,
      name: key,
      setValue: value => handleChange(key, value),
      setError: error => setError(key, error),
    };
  }
  return result as FormValues<Values>;
}

export function useFormValidationSchema<Values extends Record<string, unknown>>(
  yupSchema: UnsafeAny,
): (values: Values) => Record<keyof Values, FieldError> {
  return values => {
    try {
      yupSchema.validateSync(values, { abortEarly: false });
      return {};
    } catch (error) {
      const result: UnsafeAny = {};
      for (const err of error.inner) {
        result[err.path] = err.message;
      }
      return result;
    }
  };
}

export function useEditingField<T, TError extends FieldError = FieldError>(
  name: string,
  initialValue: T,
  validation: FieldValidation<T, TError>,
  onChange: (nextValue: T) => void,
): [Field<T, TError>, (value: T) => void, (error: TError) => void, (editing: boolean) => void] {
  const [field, setValue, setError] = useField<T, TError>(name, initialValue);
  const [editing, setEditing] = useState(false);

  const handleChange = useCallback(
    (value: T) => {
      setValue(value);
      if (!editing) {
        const error = validation(value);
        if (error) setError(error);
        else onChange(value);
      }
    },
    [editing, onChange, validation],
  );

  return [field, handleChange, setError, setEditing];
}
